import React, { useState, useEffect } from 'react'
import {
  Box, Typography, Button, TextField, Card, CardContent, IconButton,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Grid, Divider, CircularProgress, Chip
} from '@mui/material'
import { Add, Delete, ArrowBack, Save } from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { eventQuizAPI } from '../../services/eventQuizAPI'
import mediaAPI from '../../services/mediaAPI'
import toast from 'react-hot-toast'

const QUESTION_TYPES = [
  { value: 'qcm', label: 'QCM' },
  { value: 'true-false', label: 'Vrai / Faux' },
  { value: 'anagram', label: 'Anagramme' },
  { value: 'quick-answer', label: 'Réponse rapide' },
]

const emptyQuestion = (order) => ({
  type: 'qcm',
  question: '',
  options: [
    { id: 'a', text: '', isCorrect: true },
    { id: 'b', text: '', isCorrect: false },
    { id: 'c', text: '', isCorrect: false },
    { id: 'd', text: '', isCorrect: false },
  ],
  correctAnswer: '',
  points: 10,
  timeLimit: 30,
  order,
  isJackpot: false,
  media: { type: null, url: '', filename: '' }
})

export default function EventQuizForm({ mode = 'create' }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(mode === 'edit')
  const [saving, setSaving] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [questions, setQuestions] = useState([emptyQuestion(1)])
  const [settings, setSettings] = useState({
    defaultTimeLimit: 30,
    enableBonusWheel: true,
    enableSoundEffects: true,
    enableTransitions: true,
    bonusWheelMultiplier: 2
  })

  useEffect(() => {
    if (mode === 'edit' && id) loadQuiz()
  }, [id, mode])

  const loadQuiz = async () => {
    try {
      const res = await eventQuizAPI.getById(id)
      const q = res.data.quiz
      setTitle(q.title)
      setDescription(q.description)
      setQuestions(q.questions.length ? q.questions : [emptyQuestion(1)])
      setSettings(q.settings)
    } catch {
      toast.error('Quiz introuvable')
      navigate('/event-quiz')
    } finally {
      setLoading(false)
    }
  }

  const updateQuestion = (index, field, value) => {
    setQuestions(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }

      if (field === 'type') {
        if (value === 'true-false') {
          updated[index].options = [
            { id: 'true', text: 'Vrai', isCorrect: true },
            { id: 'false', text: 'Faux', isCorrect: false },
          ]
        } else if (value === 'qcm') {
          updated[index].options = emptyQuestion(1).options
        }
      }
      return updated
    })
  }

  const handleMediaUpload = async (index, file) => {
    try {
      const res = await mediaAPI.uploadFile(file)
      const fileInfo = res.file
      updateQuestion(index, 'media', {
        type: file.type.startsWith('image') ? 'image' : file.type.startsWith('audio') ? 'audio' : 'video',
        url: mediaAPI.getFullUrl(fileInfo.url),
        filename: fileInfo.filename
      })
      toast.success('Média uploadé')
    } catch {
      toast.error('Erreur upload')
    }
  }

  const handleSave = async () => {
    if (!title.trim()) return toast.error('Titre requis')
    if (questions.some(q => !q.question.trim())) return toast.error('Toutes les questions doivent avoir un texte')

    setSaving(true)
    try {
      const cleanedQuestions = questions.map(q => ({
        ...q,
        options: (q.type === 'qcm' || q.type === 'true-false') ? q.options : []
      }))
      const data = { title, description, questions: cleanedQuestions, settings, status: 'ready' }
      if (mode === 'edit') {
        await eventQuizAPI.update(id, data)
        toast.success('Quiz mis à jour')
      } else {
        await eventQuizAPI.create(data)
        toast.success('Quiz créé')
      }
      navigate('/event-quiz')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <CircularProgress />

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/event-quiz')} sx={{ mb: 2 }}>
        Retour
      </Button>

      <Typography variant="h4" gutterBottom>
        {mode === 'edit' ? 'Modifier la Compétition' : 'Nouvelle Compétition'}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Titre" value={title} onChange={(e) => setTitle(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Typography variant="h6" gutterBottom>Paramètres</Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth type="number" label="Timer par défaut (s)" value={settings.defaultTimeLimit}
                onChange={(e) => setSettings(s => ({ ...s, defaultTimeLimit: +e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel control={<Switch checked={settings.enableBonusWheel} onChange={(e) => setSettings(s => ({ ...s, enableBonusWheel: e.target.checked }))} />} label="Roue bonus" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel control={<Switch checked={settings.enableSoundEffects} onChange={(e) => setSettings(s => ({ ...s, enableSoundEffects: e.target.checked }))} />} label="Effets sonores" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel control={<Switch checked={settings.enableTransitions} onChange={(e) => setSettings(s => ({ ...s, enableTransitions: e.target.checked }))} />} label="Animations" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Questions ({questions.length})</Typography>
        <Button startIcon={<Add />} onClick={() => setQuestions(q => [...q, emptyQuestion(q.length + 1)])}>
          Ajouter
        </Button>
      </Box>

      {questions.map((q, i) => (
        <Card key={i} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Chip label={`Question ${i + 1}`} />
              <Box>
                <FormControlLabel
                  control={<Switch checked={q.isJackpot} onChange={(e) => updateQuestion(i, 'isJackpot', e.target.checked)} />}
                  label="Jackpot x2"
                />
                {questions.length > 1 && (
                  <IconButton color="error" onClick={() => setQuestions(prev => prev.filter((_, j) => j !== i))}>
                    <Delete />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select value={q.type} label="Type" onChange={(e) => updateQuestion(i, 'type', e.target.value)}>
                    {QUESTION_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth type="number" label="Points" value={q.points} onChange={(e) => updateQuestion(i, 'points', +e.target.value)} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth type="number" label="Timer (s)" value={q.timeLimit} onChange={(e) => updateQuestion(i, 'timeLimit', +e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Question" value={q.question} onChange={(e) => updateQuestion(i, 'question', e.target.value)} />
              </Grid>

              {(q.type === 'qcm' || q.type === 'true-false') && q.options?.map((opt, oi) => (
                <Grid item xs={12} sm={6} key={opt.id}>
                  <TextField
                    fullWidth
                    label={`Option ${opt.id}`}
                    value={opt.text}
                    onChange={(e) => {
                      const opts = [...q.options]
                      opts[oi] = { ...opts[oi], text: e.target.value }
                      updateQuestion(i, 'options', opts)
                    }}
                    InputProps={{
                      endAdornment: (
                        <Button size="small" variant={opt.isCorrect ? 'contained' : 'outlined'} color="success"
                          onClick={() => {
                            const opts = q.options.map((o, j) => ({ ...o, isCorrect: j === oi }))
                            updateQuestion(i, 'options', opts)
                          }}>
                          ✓
                        </Button>
                      )
                    }}
                  />
                </Grid>
              ))}

              {(q.type === 'anagram' || q.type === 'quick-answer') && (
                <Grid item xs={12}>
                  <TextField fullWidth label="Réponse correcte" value={q.correctAnswer}
                    onChange={(e) => updateQuestion(i, 'correctAnswer', e.target.value)} />
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Button variant="outlined" component="label" size="small">
                  Ajouter image / audio / vidéo
                  <input type="file" hidden accept="image/*,audio/*,video/*"
                    onChange={(e) => e.target.files[0] && handleMediaUpload(i, e.target.files[0])} />
                </Button>
                {q.media?.url && <Chip label={q.media.filename || 'Média'} sx={{ ml: 1 }} onDelete={() => updateQuestion(i, 'media', { type: null, url: '', filename: '' })} />}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Button variant="contained" size="large" startIcon={<Save />} onClick={handleSave} disabled={saving}>
        {saving ? 'Enregistrement...' : 'Enregistrer'}
      </Button>
    </Box>
  )
}
